import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { services } from "@/data/realEstateServices";
import { blogArticles } from "@/data/blogArticles";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="border-t border-border py-8">
    <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-secondary">{title}</h2>
    <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{children}</ul>
  </section>
);

const Item = ({ to, label, desc }: { to: string; label: string; desc?: string }) => (
  <li>
    <Link
      to={to}
      className="group block rounded-md border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-accent/40"
    >
      <div className="text-sm font-semibold text-primary group-hover:text-primary">{label}</div>
      {desc && <div className="text-xs text-muted-foreground">{desc}</div>}
    </Link>
  </li>
);

const SitemapPage = () => {
  return (
    <>
      <Helmet>
        <title>Site Map | Elevate Global Solutions</title>
        <meta
          name="description"
          content="Full site map of Elevate Global Solutions, every page, service, and article in one place."
        />
        <link rel="canonical" href="https://www.elevateglobalsolutions.net/sitemap" />
      </Helmet>

      <div className="bg-background">
        <div className="container-narrow section-padding">
          <header className="mb-6">
            <div className="gold-divider mb-4" />
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-primary md:text-5xl">
              Site Map
            </h1>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              Every page on elevateglobalsolutions.net, organized for quick navigation.
              Looking for the XML version for search engines?{" "}
              <a
                href="/sitemap.xml"
                className="font-medium text-primary underline underline-offset-4 hover:text-secondary"
              >
                /sitemap.xml
              </a>
              .
            </p>
          </header>

          <Section title="Main">
            <Item to="/" label="Home" desc="Acquisition operations overview" />
            <Item to="/about" label="About" desc="Who we are & how we work" />
            <Item to="/contact" label="Contact" desc="Get in touch with the team" />
            <Item to="/book" label="Book a Strategy Call" desc="Free 30-min discovery call" />
          </Section>

          <Section title="Services">
            <Item to="/services" label="All Services" desc="Full services overview" />
            {services.map((s) => (
              <Item key={s.slug} to={`/services/${s.slug}`} label={s.shortLabel} desc={s.title} />
            ))}
          </Section>

          <Section title="Resources">
            <Item to="/blog" label="Elevate Insights Blog" desc="Articles & playbooks" />
            <Item to="/coaching" label="Coaching" desc="1:1 acquisition coaching" />
            <Item to="/elevate-moment" label="The Elevate Moment" desc="A short brand experience" />
          </Section>

          <Section title="Blog Articles">
            {blogArticles.map((a) => (
              <Item key={a.slug} to={`/blog/${a.slug}`} label={a.title} />
            ))}
          </Section>

          <Section title="For Search Engines">
            <li>
              <a
                href="/sitemap.xml"
                className="block rounded-md px-3 py-2 text-sm font-semibold text-primary hover:text-secondary"
              >
                /sitemap.xml
                <span className="block text-xs font-normal text-muted-foreground">
                  XML sitemap for crawlers
                </span>
              </a>
            </li>
            <li>
              <a
                href="/robots.txt"
                className="block rounded-md px-3 py-2 text-sm font-semibold text-primary hover:text-secondary"
              >
                /robots.txt
                <span className="block text-xs font-normal text-muted-foreground">
                  Crawler access rules
                </span>
              </a>
            </li>
          </Section>
        </div>
      </div>
    </>
  );
};

export default SitemapPage;
